import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { MorphemePanelsActionsToolbar } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.toolbar";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import {
  GraphemeMorphemePanelsContext,
  GraphemeMorphemePanelsContextProvider,
  type GraphemeMorphemePanelsContextValue,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { createMaterialTopTabBarProps } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { morphemePanelTabBarIcon } from "#morpheme-panel-tab-bar-icon";
import { useIsFocused } from "@-/com.npmjs.expo-router";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { Animated, Platform, View } from "@-/com.npmjs.react-native";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import useLatest from "@-/com.npmjs.use-latest";
import { TabView } from "@-/com.npmjs.react-native-tab-view";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { MorphemeInfoPanelLoadingView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content";
import { MorphemeMnemonicsView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics";
import { GraphemeInfoView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.info";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { VisualActivityProvider } from "@-/dev.improve.haniverse.lib.visual-activity";

export type GraphemeMorphemePanelsActivePanel = "info" | "mnemonics";

interface MorphemePanelsTabRoute {
  key: string;
  name: string;
  render: () => ReactElement;
  title: string;
}

function shouldIgnoreMorphemePanelsKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tagName = target.tagName;
  if (tagName === "TEXTAREA") {
    return true;
  }
  if (tagName !== "INPUT") {
    return false;
  }
  return !target.classList.contains("persistent-keyboard-input");
}

const graphemeCardPanelTabList = [
  { segment: "info", title: "Info" },
  { segment: "mnemonics", title: "Mnemonics" },
] as const;

const MORPHEME_PANEL_SEGMENT_SUFFIX_REGEX = /\/(?:mnemonics|info)$/;

function panelIndexForPanel(panel: GraphemeMorphemePanelsActivePanel): number {
  return panel === "mnemonics" ? 1 : 0;
}

function hrefForPanel(href: string, panel: GraphemeMorphemePanelsActivePanel): string {
  return `${href.replace(MORPHEME_PANEL_SEGMENT_SUFFIX_REGEX, "")}/${panel}`;
}

export function GraphemeMorphemePanelsView({
  activePanel,
  headerExpansionContent,
  onBack,
  readonlySlice,
  userSlice,
}: {
  activePanel: GraphemeMorphemePanelsActivePanel;
  headerExpansionContent?: ReactNode;
  onBack?: () => void;
} & GraphemeMorphemePanelsContextValue) {
  return (
    <GraphemeMorphemePanelsContextProvider readonlySlice={readonlySlice} userSlice={userSlice}>
      <GraphemeMorphemePanelsViewContent
        activePanel={activePanel}
        headerExpansionContent={headerExpansionContent}
        onBack={onBack}
      />
    </GraphemeMorphemePanelsContextProvider>
  );
}

function GraphemeMorphemePanelsViewContent({
  activePanel,
  headerExpansionContent,
  onBack,
}: {
  activePanel: GraphemeMorphemePanelsActivePanel;
  headerExpansionContent?: ReactNode;
  onBack?: () => void;
}) {
  const isFocused = useIsFocused();
  const [layout] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  const morphemePanelsHrefNavigator = useMorphemePanelsHrefNavigator();
  const activePanelIndex = panelIndexForPanel(activePanel);
  const [activeIndex, setActiveIndex] = useState(activePanelIndex);
  const [morphemeHrefObject] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.morphemeHrefObject,
  );
  const [grapheme] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
  );
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = getGraphemeGlyph(grapheme);
  const href = morphemeHrefObject.href;

  // Deep links and in-app navigations can refresh the active route after mount (warm
  // Android intents, morpheme switches). Keep the pager index aligned with the route.
  useEffect(() => {
    setActiveIndex(activePanelIndex);
  }, [activePanelIndex, grapheme]);

  // Single entry point for switching the top-level panel: keep the pager index and
  // the route segment in lockstep, whether it changed via a tab tap, the pager swipe,
  // or the back-swipe gesture.
  const selectIndex = useCallback(
    (nextIndex: number) => {
      setActiveIndex(nextIndex);
      const segment = graphemeCardPanelTabList[nextIndex]?.segment;
      if (segment === undefined) {
        return;
      }
      if (segment === activePanel) {
        return;
      }
      const { view: _view, ...params } = morphemeHrefObject.params;
      // Navigation throws if the navigation store isn't ready (e.g. rendered
      // standalone in tests); activeIndex already drives the UI, so the URL sync
      // is best-effort.
      try {
        morphemePanelsHrefNavigator.replace.mutate({
          morpheme: grapheme,
          prefix: {
            href,
            params: { ...params, view: segment },
          },
        });
      } catch {
        // No active navigation container — skip the URL sync.
      }
    },
    [activePanel, grapheme, href, morphemeHrefObject.params, morphemePanelsHrefNavigator],
  );

  const mnemonicsIndex = 1;

  // Android: the mnemonics view reports whether its inner pager is on the first
  // (meaning) tab. Only there does the outer pager own the rightward back-swipe
  // to info (see `swipeEnabled` below). Default true so the first frame
  // already allows the back-swipe before the child reports in.
  const [mnemonicsOnFirstTab, setMnemonicsOnFirstTab] = useState(true);

  const tabRouteList = useMemo<MorphemePanelsTabRoute[]>(
    () =>
      graphemeCardPanelTabList.map(({ segment, title }) => {
        const route = hrefForPanel(href, segment);
        return {
          key: route + grapheme,
          name: segment,
          render:
            segment === "info"
              ? () => (
                  // Both scenes stay mounted (see `renderScene`), so a cold info slice
                  // renders its own loading state here instead of leaving the incoming page
                  // blank for the whole drag: the sticky "Overview" header with a spinner in
                  // place of the chevron. The mnemonics scene carries its own boundary inside
                  // `MorphemeMnemonicsView`.
                  <SuspensePageBoundary
                    fallback={<MorphemeInfoPanelLoadingView />}
                    location={import.meta.location}
                  >
                    <GraphemeInfoView grapheme={grapheme} />
                  </SuspensePageBoundary>
                )
              : () => (
                  <MorphemeMnemonicsView
                    onFirstTabChange={setMnemonicsOnFirstTab}
                    rawGrapheme={grapheme}
                  />
                ),
          title,
        };
      }),
    [href, grapheme],
  );

  const tabRouteByKey = useMemo(
    () => new Map(tabRouteList.map((route) => [route.key, route])),
    [tabRouteList],
  );

  const pendingToolbarIndexRef = useRef<number | null>(null);
  const selectToolbarIndex = useCallback(
    (nextIndex: number) => {
      pendingToolbarIndexRef.current = nextIndex;
      selectIndex(nextIndex);
    },
    [selectIndex],
  );
  const handlePagerIndexChange = useCallback(
    (nextIndex: number) => {
      const pendingToolbarIndex = pendingToolbarIndexRef.current;
      if (pendingToolbarIndex !== null) {
        if (nextIndex !== pendingToolbarIndex) {
          return;
        }
        pendingToolbarIndexRef.current = null;
      }
      selectIndex(nextIndex);
    },
    [selectIndex],
  );

  const handleSwipeStart = useCallback(() => {
    pendingToolbarIndexRef.current = null;
  }, []);

  // Render EVERY route's scene, not just the active one. `activeIndex` only advances in
  // `onIndexChange` — i.e. after the swipe settles — so gating on the active panel here left the
  // incoming page an empty `<View>` for the entire drag (the blank half-screen mid-swipe). Keeping
  // both mounted is what the browse/review path already does (`morpheme-panels-tabs-layout.tsx`
  // flips `lazy` off once its panels have laid out), and it's safe here because the lesson page
  // prefetches all four slices before this view mounts and `initialLayout` gives the pager a
  // non-zero width on its first frame.
  const renderScene = useCallback(
    ({
      position,
      route,
    }: {
      position: Animated.AnimatedInterpolation<number>;
      route: { key: string };
    }) => {
      const scene = tabRouteByKey.get(route.key)?.render() ?? null;
      const isFirstRoute = tabRouteList[0]?.key === route.key;
      return (
        <View className="flex-1">
          <View className="min-w-0 flex-1">
            <VisualActivityProvider active={tabRouteList[activeIndex]?.key === route.key}>
              {scene}
            </VisualActivityProvider>
          </View>
          {!isFirstRoute ? (
            <Animated.View
              className="absolute inset-y-0 left-0 w-px bg-border"
              pointerEvents="none"
              style={{
                opacity: position.interpolate({
                  extrapolate: "clamp",
                  inputRange: [0, 0.01, 0.99, 1],
                  outputRange: [0, 1, 1, 0],
                }),
              }}
            />
          ) : null}
        </View>
      );
    },
    [activeIndex, tabRouteByKey, tabRouteList],
  );

  const isFocusedRef = useLatest(isFocused);
  const activeIndexRef = useLatest(activeIndex);
  const selectIndexRef = useLatest(selectIndex);
  const panelCount = arrayLength(tabRouteList);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    return tinykeys(
      globalThis.window,
      {
        ArrowLeft: (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          const activeIndex = activeIndexRef.current;
          if (activeIndex > 0) {
            selectIndexRef.current(activeIndex - 1);
          }
          event.preventDefault();
        },
        ArrowRight: (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          const activeIndex = activeIndexRef.current;
          if (activeIndex < panelCount - 1) {
            selectIndexRef.current(activeIndex + 1);
          }
          event.preventDefault();
        },
      },
      {
        ignore: (event) => shouldIgnoreMorphemePanelsKeyboardTarget(event.target),
      },
    );
  }, [activeIndexRef, isFocusedRef, panelCount, selectIndexRef]);

  const tabBarProps = useMemo(() => {
    const routes = tabRouteList.map((route) => ({
      key: route.key,
      name: route.name,
      params: undefined,
    }));
    return createMaterialTopTabBarProps({
      activeIndex,
      getOptions: (route) => ({
        tabBarIcon: morphemePanelTabBarIcon(route.name),
        title: tabRouteList.find((entry) => entry.key === route.key)?.title ?? route.name,
      }),
      navigate(routeName: string) {
        const nextIndex = tabRouteList.findIndex((route) => route.name === routeName);
        if (nextIndex !== -1) {
          selectToolbarIndex(nextIndex);
        }
      },
      routes,
      stateKey: `morpheme-panels-${grapheme}-${href}`,
    });
  }, [activeIndex, href, grapheme, tabRouteList, selectToolbarIndex]);

  return (
    <>
      {headerExpansionContent}
      <PrimaryControlsStripTitle>
        <GraphemeFontGlyph
          className="text-center text-[36px] leading-12.5 text-foreground"
          glyph={graphemeGlyph}
        />
      </PrimaryControlsStripTitle>
      <View className="min-h-0 flex-1 flex-col bg-surface">
        <MorphemePanelsActionsToolbar {...tabBarProps} graphemeRoutePrefix={href} onBack={onBack} />
        <TabView
          initialLayout={{ width: layout.width }}
          navigationState={{
            index: activeIndex,
            routes: tabRouteList.map((route) => ({
              key: route.key,
              title: route.title,
            })),
          }}
          onIndexChange={handlePagerIndexChange}
          onSwipeStart={handleSwipeStart}
          renderScene={renderScene}
          renderTabBar={() => null}
          // Android: while the mnemonics panel is active, the inner pager owns
          // horizontal swipes EXCEPT on its first (meaning) tab — there the inner
          // pager has nothing to its left, so we let this outer pager own the drag
          // so a rightward swipe steps back to info with native finger
          // following. (`mnemonicsOnFirstTab` is reported by the mnemonics view.)
          swipeEnabled={
            isFocused &&
            !(activeIndex === mnemonicsIndex && !mnemonicsOnFirstTab && Platform.OS === "android")
          }
        />
      </View>
    </>
  );
}
